import { apiClient } from './client';
import { API_ENDPOINTS } from './endpoints';

/**
 * Offramp Quote - Fee breakdown for withdrawal
 */
export interface OfframpQuote {
  cashoutTotal: string; // Fiat amount user receives (after fees)
  cashoutSubtotal: string; // Fiat amount before fees
  sellAmount: string; // USDC amount being sold
  coinbaseFee: string; // Coinbase fee in fiat
  currency: string; // Fiat currency (USD)
}

/**
 * Offramp Transaction Status - Used after Coinbase widget
 */
export interface OfframpTransactionStatus {
  status: 'pending' | 'waiting_for_crypto' | 'crypto_received' | 'completed' | 'failed' | 'expired';
  sellAmount: string;
  sellCurrency: string;
  sellNetwork: string;
  cashoutAmount: string;
  cashoutCurrency: string;
  toAddress: string; // Coinbase address to send crypto to
  fromAddress: string;
  txHash?: string;
  transactionId: string;
  createdAt: string;
}

/**
 * Offramp Transaction - Historical withdrawal record
 */
export interface OfframpTransaction {
  status: string;
  sellCurrency: string;
  sellNetwork: string;
  sellAmount: string;
  cashoutTotal: string;
  coinbaseFee: string;
  txHash?: string;
  transactionId: string;
  walletAddress: string;
  createdAt: string;
}

/**
 * Coinbase Offramp API
 * Handles USDC to fiat withdrawals via Coinbase (bank transfer)
 */
export const offrampApi = {
  /**
   * Generate offramp URL with quote
   * Opens Coinbase widget for user to set up bank and confirm withdrawal
   *
   * @param amount - USDC amount to withdraw
   * @param country - Optional ISO 3166-1 country code (defaults to US)
   * @param subdivision - Optional ISO 3166-2 subdivision code
   * @param paymentMethod - Optional cashout method (defaults to ACH_BANK_ACCOUNT)
   */
  createUrl: async (
    amount: number,
    country?: string,
    subdivision?: string,
    paymentMethod?: string
  ): Promise<{ offrampUrl: string; quote: OfframpQuote; quoteId: string }> => {
    const response = await apiClient.post(API_ENDPOINTS.OFFRAMP.CREATE_URL, {
      amount,
      country,
      subdivision,
      paymentMethod,
    });
    return {
      offrampUrl: response.data.offrampUrl,
      quote: response.data.quote,
      quoteId: response.data.quoteId,
    };
  },

  /**
   * Get latest transaction status
   * Used to poll for Coinbase deposit address after user initiates offramp
   */
  getTransactionStatus: async (): Promise<{
    hasTransaction: boolean;
    transaction: OfframpTransactionStatus | null;
  }> => {
    const response = await apiClient.get(API_ENDPOINTS.OFFRAMP.TRANSACTION_STATUS);
    return {
      hasTransaction: response.data.hasTransaction,
      transaction: response.data.transaction,
    };
  },

  /**
   * Get user's offramp transaction history
   *
   * @param limit - Number of transactions to return (default: 10)
   */
  getTransactions: async (limit: number = 10): Promise<OfframpTransaction[]> => {
    const response = await apiClient.get(API_ENDPOINTS.OFFRAMP.TRANSACTIONS, {
      params: { limit },
    });
    return response.data.transactions || [];
  },

  /**
   * Check if offramp service is available
   * Returns false if CDP API credentials are not configured
   */
  getStatus: async (): Promise<{
    available: boolean;
    message: string;
    limits: { min: number; max: number };
  }> => {
    const response = await apiClient.get(API_ENDPOINTS.OFFRAMP.STATUS);
    return response.data;
  },

  /**
   * Get pending transaction status
   * Used for polling after initiating withdrawal to detect success/failure
   */
  getPendingStatus: async (): Promise<{ transaction: any | null }> => {
    const response = await apiClient.get(API_ENDPOINTS.OFFRAMP.PENDING_STATUS);
    return { transaction: response.data.transaction };
  },
};
